import { AfterViewInit, Component, EventEmitter, Input, OnInit, Output, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import { Router } from '@angular/router';

// Angular Material
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatProgressBarModule } from '@angular/material/progress-bar';

import { MatDialog, MatDialogModule } from '@angular/material/dialog';

// Shared
import { BreadcrumbComponent } from '@app/lib/components/breadcrumb/breadcrumb.component';
import { BaseHttpService } from '@app/lib/services/base-http.service';
import { AppConfiguration } from '@app/lib/services/app-config.service';
import { DialogService } from '@app/lib/services/dialog.service';

// Models and Configs
import { MASTER_DETAIL_TABLE_CONFIGS } from './configs/master-detail-configs.constants';
import { IMasterDetailTableConfig, IChildDetailTableConfig } from './models/master-detail-config.model';
import { ISearchFieldConfig, ITableColumnConfig, ITableColumnGroup, ISearchTableSavePayload } from './../configurable-search-table/models/search-table-config.model';
import { ConfirmationDialogComponent } from '@app/confirmation-dialog/confirmation-dialog.component';
import { MatSort, MatSortModule } from '@angular/material/sort';

@Component({
    selector: 'app-configurable-master-detail-table',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        HttpClientModule,
        MatTableModule,
        MatButtonModule,
        MatIconModule,
        MatInputModule,
        MatSelectModule,
        MatCheckboxModule,
        MatDatepickerModule,
        BreadcrumbComponent,
        MatProgressBarModule,
        MatDialogModule,
        MatSortModule
    ],
    templateUrl: './configurable-master-detail-table.component.html',
    styleUrl: './configurable-master-detail-table.component.scss'
})
export class ConfigurableMasterDetailTableComponent extends BaseHttpService implements OnInit, AfterViewInit {
    public config!: IMasterDetailTableConfig;

    @Input() initialData: any[] = [];
    @Output() onSearch = new EventEmitter<any>();
    @Output() onMasterSave = new EventEmitter<ISearchTableSavePayload>();
    @Output() onDetailSave = new EventEmitter<{ masterRow: any, payload: ISearchTableSavePayload }>();
    @ViewChild('masterSort') masterSort!: MatSort;
    @ViewChild('detailSort') detailSort!: MatSort;
    @ViewChild('childSort') childSort!: MatSort;
    @ViewChildren(MatSort) sorts!: QueryList<MatSort>;
    searchForm!: FormGroup;

    // Master Properties
    masterDataSource = new MatTableDataSource<any>();
    masterDisplayedColumns: string[] = [];
    isMasterAddMode = false;
    isMasterEditMode = false;
    selectedMasterRows: any[] = [];

    // Detail Properties
    detailDataSource = new MatTableDataSource<any>();
    detailDisplayedColumns: string[] = [];
    isDetailAddMode = false;
    isDetailEditMode = false;
    selectedDetailRows: any[] = [];
    singleSelectedDetailRow: any = null;

    // Child Detail Properties
    childDetailDataSource = new MatTableDataSource<any>();
    childDetailDisplayedColumns: string[] = [];
    isChildDetailAddMode = false;
    isChildDetailEditMode = false;
    selectedChildDetailRows: any[] = [];

    // Shared
    isLoading = false;
    timeOptions: string[] = [];
    singleSelectedMasterRow: any = null;

    constructor(
        private fb: FormBuilder,
        protected httpClient: HttpClient,
        appConfig: AppConfiguration,
        private router: Router,
        private dialog: MatDialog,
        private dialogService: DialogService
    ) {
        super(httpClient, appConfig);
        this.resolveConfig();
    }

    resolveConfig(): void {
        const webAddress = this.router.url.split('/').filter(Boolean).pop() || '';
        this.config = MASTER_DETAIL_TABLE_CONFIGS[webAddress];
        if (!this.config) {
            console.warn(`No configuration found for web address: ${webAddress} in MASTER_DETAIL_TABLE_CONFIGS`);
        }
    }

    ngOnInit(): void {
        if (!this.config) return;
        this.generateTimeOptions();
        this.initSearchForm();
        this.buildDisplayedColumns();
        this.fetchDropdownOptions();
    }

    ngAfterViewInit() {
        this.sorts.changes.subscribe(() => {
            this.applySorting();
        });

        this.applySorting();
    }

    applySorting() {
        const sortArray = this.sorts.toArray();

        if (sortArray[0]) {
            this.masterDataSource.sort = sortArray[0];
        }

        if (sortArray[1]) {
            this.detailDataSource.sort = sortArray[1];
        }

        if (sortArray[2]) {
            this.childDetailDataSource.sort = sortArray[2];
        }
    }

    ngOnChanges(): void {
        if (this.initialData && this.initialData.length > 0) {
            this.masterDataSource.data = this.initialData;
        }
    }

    private generateTimeOptions(): void {
        this.timeOptions = [];
        for (let h = 0; h < 24; h++) {
            for (let m = 0; m < 60; m++) {
                const hh = h.toString().padStart(2, '0');
                const mm = m.toString().padStart(2, '0');
                this.timeOptions.push(`${hh}:${mm}`);
            }
        }
    }

    initSearchForm(): void {
        const group: any = {};
        this.config.searchFields.forEach((field: ISearchFieldConfig) => {
            if (field.type === 'datetime') {
                group[`${field.key}_date`] = [''];
                group[`${field.key}_time`] = [''];
            } else {
                group[field.key] = [''];
            }
        });
        this.searchForm = this.fb.group(group);
    }

    buildDisplayedColumns(): void {
        this.masterDisplayedColumns = ['select'];
        this.config.masterTable.columns.forEach((col: ITableColumnConfig | ITableColumnGroup) => {
            if ('columns' in col) {
                col.columns.forEach((subCol: ITableColumnConfig) => this.masterDisplayedColumns.push(subCol.key));
            } else {
                this.masterDisplayedColumns.push(col.key);
            }
        });

        this.detailDisplayedColumns = ['select'];
        this.config.detailTable.columns.forEach((col: ITableColumnConfig | ITableColumnGroup) => {
            if ('columns' in col) {
                col.columns.forEach((subCol: ITableColumnConfig) => this.detailDisplayedColumns.push(subCol.key));
            } else {
                this.detailDisplayedColumns.push(col.key);
            }
        });

        if (this.config.childDetailTable) {
            this.childDetailDisplayedColumns = ['select'];
            this.config.childDetailTable.columns.forEach((col: ITableColumnConfig | ITableColumnGroup) => {
                if ('columns' in col) {
                    col.columns.forEach((subCol: ITableColumnConfig) => this.childDetailDisplayedColumns.push(subCol.key));
                } else {
                    this.childDetailDisplayedColumns.push(col.key);
                }
            });
        }
    }

    get flatMasterColumns(): ITableColumnConfig[] {
        let cols: ITableColumnConfig[] = [];
        this.config.masterTable.columns.forEach(c => {
            if ('columns' in c) cols.push(...c.columns);
            else cols.push(c);
        });
        return cols;
    }

    get flatDetailColumns(): ITableColumnConfig[] {
        let cols: ITableColumnConfig[] = [];
        this.config.detailTable.columns.forEach(c => {
            if ('columns' in c) cols.push(...c.columns);
            else cols.push(c);
        });
        return cols;
    }

    get flatChildDetailColumns(): ITableColumnConfig[] {
        if (!this.config.childDetailTable) return [];
        let cols: ITableColumnConfig[] = [];
        this.config.childDetailTable.columns.forEach(c => {
            if ('columns' in c) cols.push(...c.columns);
            else cols.push(c);
        });
        return cols;
    }

    fetchDropdownOptions(): void {
        this.config.searchFields.forEach(field => {
            if (field.type === 'select' && field.endpoint) this.loadOptionsForField(field);
        });
        this.flatMasterColumns.forEach(col => {
            if (col.type === 'select' && col.endpoint) this.loadOptionsForField(col);
        });
        this.flatDetailColumns.forEach(col => {
            if (col.type === 'select' && col.endpoint) this.loadOptionsForField(col);
        });
        this.flatChildDetailColumns.forEach(col => {
            if (col.type === 'select' && col.endpoint) this.loadOptionsForField(col);
        });
    }

    private loadOptionsForField(field: any): void {
        if (!field.endpoint) return;
        const method = field.endpointMethod || 'GET';
        const req = method === 'GET' ? this.get<any>(field.endpoint) : this.post<any, any>(field.endpoint, {});

        req.subscribe({
            next: (res: any) => {
                const dataArray = res?.success || res?.data || res || [];
                if (Array.isArray(dataArray)) {
                    field.options = dataArray.map((item: any) => {
                        if (typeof item !== 'object') return { value: item, label: String(item) };
                        const getLabel = () => {
                            if (field.endpointLabelKey) {
                                return field.endpointLabelKey.split(',').map((k: string) => item[k.trim()]).filter((val: any) => val !== undefined && val !== null).join('--');
                            }
                            return item.name || item.label;
                        };
                        return {
                            value: field.endpointValueKey ? item[field.endpointValueKey] : (item.id || item.value),
                            label: getLabel(),
                            originalObject: item // Store original to fetch dependent fields
                        };
                    });
                }
            },
            error: (err: any) => console.error(`Failed to fetch options for ${field.key}`, err)
        });
    }

    private formatDateToDDMMYYYY(date: any): string {
        if (!date) return '';
        const d = new Date(date);
        if (isNaN(d.getTime())) return date;
        const day = String(d.getDate()).padStart(2, '0');
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const year = d.getFullYear();
        return `${day}-${month}-${year}`;
    }

    private processPayloadDates(row: any, columns: ITableColumnConfig[]): any {
        const cleaned = { ...row };
        columns.forEach(col => {
            if (col.type === 'date' && cleaned[col.key]) {
                cleaned[col.key] = this.formatDateToDDMMYYYY(cleaned[col.key]);
            }
        });
        return cleaned;
    }

    private validateTextFields(row: any, columns: ITableColumnConfig[]): { isValid: boolean, errorMessage: string } {
        for (const col of columns) {
            const val = row[col.key];
            if (col.required && (val === null || val === undefined || val === '' || (typeof val === 'string' && val.trim() === ''))) {
                return { isValid: false, errorMessage: `${col.label} is a mandatory field.` };
            }
            if (typeof val === 'string' && val.length > 0) {
                if (val.trim() === '') {
                    return { isValid: false, errorMessage: `${col.label} cannot be only spaces.` };
                }
                if (/\s\s+/.test(val)) {
                    return { isValid: false, errorMessage: `${col.label} cannot contain multiple consecutive spaces.` };
                }
            }
        }
        return { isValid: true, errorMessage: '' };
    }

    // --- Search Logic ---


    handleSearch(): void {
        let payload = { ...this.searchForm.value };
        this.config.searchFields.forEach(field => {
            if (field.type === 'datetime') {
                const dateVal = payload[`${field.key}_date`];
                const timeVal = payload[`${field.key}_time`];
                if (dateVal) {
                    const d = new Date(dateVal);
                    if (timeVal) {
                        const [hh, mm] = timeVal.split(':');
                        d.setHours(parseInt(hh, 10) || 0, parseInt(mm, 10) || 0, 0, 0);
                    }
                    payload[field.key] = d.toISOString();
                }
                delete payload[`${field.key}_date`];
                delete payload[`${field.key}_time`];
            } else if (field.type === 'date') {
                const dateVal = payload[field.key];
                if (dateVal) {
                    payload[field.key] = this.formatDateToDDMMYYYY(dateVal);
                }
            }
        });

        if (this.config.payloadBuilders?.search) {
            payload = this.config.payloadBuilders.search(payload);
        }

        this.onSearch.emit(payload);

        if (this.config.apiEndpoints?.search) {
            this.isLoading = true;
            const method = this.config.apiEndpoints.searchMethod || 'POST';
            const req = method === 'GET'
                ? this.get<any>(this.config.apiEndpoints.search, { params: payload })
                : this.post<any, any>(this.config.apiEndpoints.search, payload);

            req.subscribe({
                next: (res: any) => {
                    this.masterDataSource.data = res?.success || res?.data || res || [];
                    this.resetMasterState();
                    if (this.masterDataSource.data.length === 0) {
                        this.showDialog('Information', 'No Data Exist');
                    }
                },
                error: (err: any) => {
                    console.error('Search Failed', err);
                    this.isLoading = false;
                }
            });
        }
    }

    handleReset(): void {
        this.searchForm.reset();
        this.masterDataSource.data = [];
        this.resetMasterState();
    }

    resetMasterState(): void {
        this.isLoading = false;
        this.isMasterAddMode = false;
        this.isMasterEditMode = false;
        this.selectedMasterRows = [];
        this.singleSelectedMasterRow = null;
        this.detailDataSource.data = [];
        this.resetDetailState();
    }

    resetDetailState(): void {
        this.isDetailAddMode = false;
        this.isDetailEditMode = false;
        this.selectedDetailRows = [];
        this.singleSelectedDetailRow = null;
        this.childDetailDataSource.data = [];
        this.resetChildDetailState();
    }

    resetChildDetailState(): void {
        this.isChildDetailAddMode = false;
        this.isChildDetailEditMode = false;
        this.selectedChildDetailRows = [];
    }

    private syncCurrentChildDetailToSelectedRow(): void {
        if (this.singleSelectedDetailRow && this.config?.childDetailTable) {
            const childDetailKey = this.config.detailTable.childDetailDataKey || 'childData';
            if (!this.singleSelectedDetailRow[childDetailKey]) {
                this.singleSelectedDetailRow[childDetailKey] = [];
            }
            this.singleSelectedDetailRow[childDetailKey] = this.childDetailDataSource.data.map(r => {
                const c = { ...r };
                if (r._isNew) c.transType = 'I';
                if (r._isEdit && !r._isNew) c.transType = c.transType || 'U';
                delete c._isNew;
                delete c._isEdit;
                return c;
            });
        }
    }

    private focusFirstInputInContainer(containerSelector: string): void {
        setTimeout(() => {
            const container = document.querySelector(containerSelector);
            if (!container) return;

            // Scroll table container wrapper to top so the 1st row is fully visible
            const tableWrapper = container.querySelector('.responsive-table-wrapper');
            if (tableWrapper) {
                tableWrapper.scrollTop = 0;
            }

            // Scroll the section into view at the top
            container.scrollIntoView({ behavior: 'smooth', block: 'start' });

            const firstRow = container.querySelector('table tr.mat-row, table tr:not(.mat-header-row)');
            const target = firstRow
                ? (firstRow.querySelector('input:not([disabled]):not([type="checkbox"]), mat-select:not([aria-disabled="true"])') as HTMLElement)
                : (container.querySelector('input:not([disabled]):not([type="checkbox"]), mat-select:not([aria-disabled="true"])') as HTMLElement);

            if (target) {
                target.focus({ preventScroll: true });
            }
        }, 150);
    }

    selectDetailRow(row: any): void {
        if (this.singleSelectedDetailRow === row) return;
        this.syncCurrentChildDetailToSelectedRow();
        this.singleSelectedDetailRow = row;
        this.selectedDetailRows = [row];
        if (this.config.childDetailTable) {
            const childDetailKey = this.config.detailTable.childDetailDataKey || 'childData';
            this.childDetailDataSource.data = row[childDetailKey] || [];
            this.resetChildDetailState();
        }
    }

    // --- Master Table Interactions ---
    onMasterRowClick(row: any): void {
        if (this.isMasterAddMode || this.isMasterEditMode) return;
        this.singleSelectedMasterRow = row;
        const detailKey = this.config.masterTable.detailDataKey;
        this.detailDataSource.data = row[detailKey] || [];
        this.resetDetailState();
    }

    getMasterDisplayName(): string {
        if (!this.singleSelectedMasterRow) return '';
        // Try to guess a display name properties if existing
        return this.singleSelectedMasterRow.name || this.singleSelectedMasterRow.courtUnitTypeName || this.singleSelectedMasterRow.id || '';
    }

    toggleMasterSelection(row: any, checked: boolean): void {
        if (checked) {
            // Enforce single selection
            this.selectedMasterRows = [row];
            this.singleSelectedMasterRow = row;
            this.onMasterRowClick(row);
        } else {
            this.selectedMasterRows = this.selectedMasterRows.filter(r => r !== row);
            if (this.selectedMasterRows.length === 0) {
                this.singleSelectedMasterRow = null;
                this.detailDataSource.data = [];
            }
        }
    }

    isMasterSelected(row: any): boolean {
        // Only return true if it is strictly the currently selected single row
        return this.singleSelectedMasterRow === row;
    }

    handleMasterAdd(): void {
        this.isMasterAddMode = true;
        const newRow: any = { _isNew: true, _isEdit: true };
        this.detailDataSource.data = []; // clear detail view while adding master
        const detailKey = this.config.masterTable.detailDataKey;
        if (detailKey) newRow[detailKey] = [];
        this.masterDataSource.data = [newRow, ...this.masterDataSource.data];
        this.selectedMasterRows = [newRow];
        this.singleSelectedMasterRow = newRow;
        this.focusFirstInputInContainer('.master-table-container');
    }

    handleMasterModify(): void {
        if (this.selectedMasterRows.length === 0) return;
        this.isMasterEditMode = true;
        this.selectedMasterRows.forEach(row => { row._isEdit = true; });
        this.focusFirstInputInContainer('.master-table-container');
    }

    handleMasterCancel(): void {
        this.isMasterAddMode = false;
        this.isMasterEditMode = false;
        this.masterDataSource.data.forEach(row => row._isEdit = false);
        this.masterDataSource.data = this.masterDataSource.data.filter(row => !row._isNew);
        this.selectedMasterRows = [];
        this.singleSelectedMasterRow = null;
        this.detailDataSource.data = [];
        this.singleSelectedDetailRow = null;
        this.childDetailDataSource.data = [];
    }

    // --- Dialog Helper ---
    private showDialog(title: string, message: string): void {
        this.dialogService.openDialog1({
            data: {
                title: title,
                message: message,
                icon: title.toLowerCase().includes('error') ? 'error' : title.toLowerCase().includes('information') ? 'info' : 'check_small' // ✅ Uses error icon if title has 'error'
            },
            disableClose: true,
            hasBackdrop: true,
            backdropClass: 'dialog-backdrop',
        });
    }

    handleMasterSave(): void {
        const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
            data: {
                title: 'Confirm Action',
                message: "Are you sure you want to save?",
            }, disableClose: true,
            hasBackdrop: true,
            backdropClass: 'dialog-backdrop',
        });

        dialogRef.afterClosed().subscribe((result) => {
            if (result) {
                this.executeMasterSave();
            }
        });
    }

    private executeMasterSave(): void {
        const detailKey = this.config.masterTable.detailDataKey;

        // Sync active child detail data to single selected detail row
        this.syncCurrentChildDetailToSelectedRow();

        // Sync active detail data to master row before validation if there is a single selection
        if (this.singleSelectedMasterRow) {
            this.singleSelectedMasterRow[detailKey] = this.detailDataSource.data.map(r => {
                const c = { ...r };
                if (r._isNew) c.transType = 'I';
                if (r._isEdit && !r._isNew) c.transType = c.transType || 'U';
                delete c._isNew;
                delete c._isEdit;
                return c;
            });
        }

        const newRecs: any[] = [];
        const modRecs: any[] = [];
        let isValid = true;
        let errorMessage = '';

        for (const row of this.masterDataSource.data) {
            if (row._isNew || row._isEdit) {
                const validation = this.validateTextFields(row, this.flatMasterColumns);
                if (!validation.isValid) {
                    isValid = false;
                    errorMessage = validation.errorMessage;
                    break;
                }
            }
            if (!isValid) break;

            // Validate active detail rows for the currently selected master row
            if (this.singleSelectedMasterRow === row) {
                for (const detailRow of this.detailDataSource.data) {
                    if (detailRow._isNew || detailRow._isEdit) {
                        const validation = this.validateTextFields(detailRow, this.flatDetailColumns);
                        if (!validation.isValid) {
                            isValid = false;
                            errorMessage = validation.errorMessage;
                            break;
                        }
                    }
                    if (!isValid) break;

                    if (this.singleSelectedDetailRow === detailRow && this.config.childDetailTable) {
                        const childDetailKey = this.config.detailTable.childDetailDataKey || 'childData';
                        const childRows = childDetailKey ? detailRow[childDetailKey] || [] : [];

                        const isChildRequired = this.config.detailTable.childDetailDataRequired ?? true; // Default to true if not specified, keeping existing behavior

                        if (isChildRequired && (detailRow._isNew || detailRow._isEdit) && childRows.length === 0 && this.childDetailDataSource.data.length === 0) {
                            isValid = false;
                            errorMessage = `Please add at least one ${this.config.childDetailTable.panelTitle} record for the selected detail.`;
                            break;
                        }

                        for (const childRow of this.childDetailDataSource.data) {
                            if (childRow._isNew || childRow._isEdit) {
                                const validation = this.validateTextFields(childRow, this.flatChildDetailColumns);
                                if (!validation.isValid) {
                                    isValid = false;
                                    errorMessage = validation.errorMessage;
                                    break;
                                }
                            }
                            if (!isValid) break;
                        }
                    }
                    if (!isValid) break;
                }
            }
            if (!isValid) break;
        }

        if (!isValid) {
            this.showDialog('Validation Error', errorMessage);
            return;
        }

        const rowStrings = new Set<string>();
        for (const row of this.masterDataSource.data) {
            const rowObj: any = {};
            this.flatMasterColumns.forEach((c: any) => rowObj[c.key] = row[c.key]);
            const rowStr = JSON.stringify(rowObj);
            if (rowStrings.has(rowStr)) {
                this.showDialog('Information', 'Duplicate entry found in Master data. Cannot save.');
                return;
            }
            rowStrings.add(rowStr);

            if (this.singleSelectedMasterRow === row) {
                const detailStrings = new Set<string>();
                for (const detailRow of this.detailDataSource.data) {
                    const detailObj: any = {};
                    this.flatDetailColumns.forEach((c: any) => detailObj[c.key] = detailRow[c.key]);
                    const detailStr = JSON.stringify(detailObj);
                    if (detailStrings.has(detailStr)) {
                        this.showDialog('Information', `Duplicate entry found in ${this.config.detailTable.panelTitle}. Cannot save.`);
                        return;
                    }
                    detailStrings.add(detailStr);

                    if (this.config.childDetailTable && this.singleSelectedDetailRow === detailRow) {
                        const childStrings = new Set<string>();
                        for (const childRow of this.childDetailDataSource.data) {
                            const childObj: any = {};
                            this.flatChildDetailColumns.forEach((c: any) => childObj[c.key] = childRow[c.key]);
                            const childStr = JSON.stringify(childObj);
                            if (childStrings.has(childStr)) {
                                this.showDialog('Validation Error', `Duplicate entry found in ${this.config.childDetailTable.panelTitle}. Cannot save.`);
                                return;
                            }
                            childStrings.add(childStr);
                        }
                    }
                }
            }
        }

        this.masterDataSource.data.forEach(row => {
            const cleanRow = { ...row };
            delete cleanRow._isNew;
            delete cleanRow._isEdit;

            const childDetailKey = this.config.detailTable.childDetailDataKey || 'childData';
            const hasSubEdits = (this.singleSelectedMasterRow === row) &&
                (this.isDetailAddMode || this.isDetailEditMode || this.isChildDetailAddMode || this.isChildDetailEditMode);

            if (row._isNew) {
                const isDetailRequired = this.config.masterTable.detailDataRequired ?? true; // Default to true if not specified
                if (isDetailRequired && (!cleanRow[detailKey] || cleanRow[detailKey].length === 0)) {
                    isValid = false;
                    errorMessage = `Please add at least ${this.config.detailTable.panelTitle} record.`;
                    return;
                }
                cleanRow.transType = 'I';
                if (cleanRow[detailKey] && Array.isArray(cleanRow[detailKey])) {
                    cleanRow[detailKey] = cleanRow[detailKey].map((d: any) => {
                        let detailRow = { ...d };
                        if (row._isNew || d._isNew) detailRow.transType = 'I';
                        else detailRow.transType = detailRow.transType || 'U';
                        delete detailRow._isNew;
                        delete detailRow._isEdit;
                        detailRow = this.processPayloadDates(detailRow, this.flatDetailColumns);
                        if (this.config.childDetailTable && detailRow[childDetailKey] && Array.isArray(detailRow[childDetailKey])) {
                            detailRow[childDetailKey] = detailRow[childDetailKey].map((cd: any) => {
                                let childRow = { ...cd };
                                if (row._isNew || d._isNew || cd._isNew) childRow.transType = 'I';
                                else childRow.transType = childRow.transType || 'U';
                                delete childRow._isNew;
                                delete childRow._isEdit;
                                childRow = this.processPayloadDates(childRow, this.flatChildDetailColumns);
                                return childRow;
                            });
                        }
                        return detailRow;
                    });
                }
                const formattedMaster = this.processPayloadDates(cleanRow, this.flatMasterColumns);
                newRecs.push(formattedMaster);
            }
            else if (row._isEdit || hasSubEdits) {
                cleanRow.transType = cleanRow.transType || 'U';
                if (cleanRow[detailKey] && Array.isArray(cleanRow[detailKey])) {
                    cleanRow[detailKey] = cleanRow[detailKey].map((d: any) => {
                        let detailRow = { ...d, transType: d.transType || 'U' };
                        detailRow = this.processPayloadDates(detailRow, this.flatDetailColumns);
                        if (this.config.childDetailTable && detailRow[childDetailKey] && Array.isArray(detailRow[childDetailKey])) {
                            detailRow[childDetailKey] = detailRow[childDetailKey].map((cd: any) => {
                                let childRow = { ...cd, transType: cd.transType || 'U' };
                                childRow = this.processPayloadDates(childRow, this.flatChildDetailColumns);
                                return childRow;
                            });
                        }
                        return detailRow;
                    });
                }
                const formattedMaster = this.processPayloadDates(cleanRow, this.flatMasterColumns);
                modRecs.push(formattedMaster);
            }
        });

        if (!isValid) {
            this.showDialog('Information', errorMessage);
            return;
        }

        let addPayload = this.config.payloadBuilders?.insertMaster ? this.config.payloadBuilders.insertMaster(newRecs) : newRecs;
        let updatePayload = this.config.payloadBuilders?.updateMaster ? this.config.payloadBuilders.updateMaster(modRecs) : modRecs;

        const payload: ISearchTableSavePayload = { newRecords: addPayload, modifiedRecords: updatePayload, deletedRecords: [] };
        this.onMasterSave.emit(payload);

        if (this.isMasterEditMode && this.config.apiEndpoints?.updateMaster) {
            this.executeApiCall(this.config.apiEndpoints.updateMaster, updatePayload, this.config.apiEndpoints.updateMasterMethod, 'master');
        } else if (this.config.apiEndpoints?.saveMaster) {
            let combinedPayload = payload;
            if (this.config.payloadBuilders?.saveMaster) combinedPayload = this.config.payloadBuilders.saveMaster(newRecs, modRecs, []);
            this.executeApiCall(this.config.apiEndpoints.saveMaster, combinedPayload, this.config.apiEndpoints.saveMasterMethod, 'master');
        } else {
            if (this.config.apiEndpoints?.addMaster && newRecs.length > 0) this.executeApiCall(this.config.apiEndpoints.addMaster, addPayload, this.config.apiEndpoints.addMasterMethod, 'master');
            if (this.config.apiEndpoints?.updateMaster && modRecs.length > 0) this.executeApiCall(this.config.apiEndpoints.updateMaster, updatePayload, this.config.apiEndpoints.updateMasterMethod, 'master');
        }

        // To reflect updates visually immediately without refresh
        this.masterDataSource.data.forEach(row => { row._isEdit = false; row._isNew = false; });
        this.isMasterAddMode = false;
        this.isMasterEditMode = false;

        if (this.singleSelectedMasterRow) {
            this.detailDataSource.data.forEach(row => { row._isEdit = false; row._isNew = false; });
            this.isDetailAddMode = false;
            this.isDetailEditMode = false;

            if (this.singleSelectedDetailRow) {
                this.childDetailDataSource.data.forEach(row => { row._isEdit = false; row._isNew = false; });
                this.isChildDetailAddMode = false;
                this.isChildDetailEditMode = false;
            }
        }
    }
    onMasterFieldChange(row: any, key: string): void {

        // ✅ Only apply for Court Divisions page
        if (this.config.pageTitle !== 'Court Divisions') return;

        // ✅ Check if changed field is division name
        if (key === 'cutName') {

            const newName = row['cutName'];

            // ✅ Update detail rows linked to this master
            this.detailDataSource.data.forEach((detailRow, index) => {

                // Optional: regenerate with index
                detailRow['crtDivName'] = `${newName}-Court ${index + 1}`;

                // OR if you want exact same name (no numbering)
                // detailRow['courtDivisionName'] = newName;
            });

            // ✅ Refresh UI
            this.detailDataSource._updateChangeSubscription();
        }
    }
    handleMasterDelete(): void {
        if (this.selectedMasterRows.length === 0) return;

        const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
            data: {
                title: 'Confirm Action',
                message: "Are you sure you want to delete?",
            }, disableClose: true,
            hasBackdrop: true,
            backdropClass: 'dialog-backdrop',
        });

        dialogRef.afterClosed().subscribe((result) => {
            if (result) {
                this.executeMasterDelete();
            }
        });
    }

    private executeMasterDelete(): void {
        const isChildPartialDelete = !!(this.singleSelectedDetailRow && this.selectedChildDetailRows.length > 0);
        const isDetailPartialDelete = !!(this.singleSelectedMasterRow && this.selectedDetailRows.length > 0);
        const isPartialDelete = isChildPartialDelete || isDetailPartialDelete;

        const delRecs = this.selectedMasterRows.map(row => {
            const cleanRow = { ...row };
            delete cleanRow._isNew; delete cleanRow._isEdit;

            const detailKey = this.config.masterTable.detailDataKey;
            const childDetailKey = this.config.detailTable.childDetailDataKey || 'childData';

            if (this.singleSelectedMasterRow === row) {
                if (isChildPartialDelete) {
                    const detailRowCopy = { ...this.singleSelectedDetailRow };
                    delete detailRowCopy._isNew; delete detailRowCopy._isEdit;
                    detailRowCopy[childDetailKey] = this.selectedChildDetailRows.map(r => {
                        const c = { ...r };
                        delete c._isNew; delete c._isEdit;
                        return c;
                    });
                    cleanRow[detailKey] = [detailRowCopy];
                } else if (isDetailPartialDelete) {
                    cleanRow[detailKey] = this.selectedDetailRows.map(r => {
                        const c = { ...r };
                        delete c._isNew; delete c._isEdit;
                        if (childDetailKey) c[childDetailKey] = [];
                        return c;
                    });
                } else {
                    cleanRow[detailKey] = [];
                }
            } else {
                cleanRow[detailKey] = [];
            }

            return cleanRow;
        });

        const detailRowsForDelete = isDetailPartialDelete
            ? this.selectedDetailRows
            : (isChildPartialDelete && this.singleSelectedDetailRow ? [this.singleSelectedDetailRow] : []);

        let deletePayload = this.config.payloadBuilders?.deleteMaster
            ? (this.config.payloadBuilders.deleteMaster as any)(delRecs, detailRowsForDelete, this.selectedChildDetailRows)
            : delRecs;

        this.onMasterSave.emit({ newRecords: [], modifiedRecords: [], deletedRecords: deletePayload });

        if (this.config.apiEndpoints?.deleteMaster) {
            this.executeApiCall(this.config.apiEndpoints.deleteMaster, deletePayload, this.config.apiEndpoints.deleteMasterMethod, 'master', 'delete');
        } else {
            console.warn('No deleteMaster endpoint configured');
        }

        // Remove from UI immediately
        if (isChildPartialDelete) {
            this.childDetailDataSource.data = this.childDetailDataSource.data.filter(r => !this.selectedChildDetailRows.includes(r));
            this.selectedChildDetailRows = [];
            this.syncCurrentChildDetailToSelectedRow();
        } else if (isDetailPartialDelete) {
            this.detailDataSource.data = this.detailDataSource.data.filter(r => !this.selectedDetailRows.includes(r));
            const detailKey = this.config.masterTable.detailDataKey;
            this.singleSelectedMasterRow[detailKey] = this.detailDataSource.data.map(r => {
                const c = { ...r };
                delete c._isNew;
                delete c._isEdit;
                return c;
            });
            this.selectedDetailRows = [];
            this.singleSelectedDetailRow = null;
            this.childDetailDataSource.data = [];
        } else {
            this.masterDataSource.data = this.masterDataSource.data.filter(r => !this.selectedMasterRows.includes(r));
            this.selectedMasterRows = [];
            this.singleSelectedMasterRow = null;
            this.detailDataSource.data = [];
            this.singleSelectedDetailRow = null;
            this.childDetailDataSource.data = [];
        }
    }

    // --- Detail Table Interactions ---
    onDetailRowClick(row: any): void {
        if (this.config.childDetailTable) {
            this.selectDetailRow(row);
            return;
        }
        if (this.isDetailAddMode || this.isDetailEditMode) return;
        this.singleSelectedDetailRow = row;
    }

    getDetailDisplayName(): string {
        if (!this.singleSelectedDetailRow) return '';
        // Try to guess a display name properties if existing
        return this.singleSelectedDetailRow.name || this.singleSelectedDetailRow.id || '';
    }

    toggleDetailSelection(row: any, checked: boolean): void {
        if (checked) {
            // Enforce single selection if child table enabled
            if (this.config.childDetailTable) {
                this.selectDetailRow(row);
            } else {
                this.selectedDetailRows.push(row);
            }
        } else {
            this.selectedDetailRows = this.selectedDetailRows.filter(r => r !== row);
            if (this.selectedDetailRows.length === 0) {
                this.syncCurrentChildDetailToSelectedRow();
                this.singleSelectedDetailRow = null;
                this.childDetailDataSource.data = [];
            }
        }
    }

    isDetailSelected(row: any): boolean {
        // Enforce tight match if child detail is active
        if (this.config.childDetailTable) {
            return this.singleSelectedDetailRow === row;
        }
        return this.selectedDetailRows.includes(row);
    }

    handleDetailAdd(): void {
        this.syncCurrentChildDetailToSelectedRow();
        this.isDetailAddMode = true;
        const newRow: any = { _isNew: true, _isEdit: true };
        const childDetailKey = this.config.detailTable.childDetailDataKey || 'childData';
        if (this.config.childDetailTable && childDetailKey) {
            newRow[childDetailKey] = [];
        }
        if (this.config.pageTitle === 'Court Divisions' && this.singleSelectedMasterRow) {
            const masterName = this.singleSelectedMasterRow['cutName']; // 🔁 change key if needed
            const existingCount = this.detailDataSource.data.length;
            newRow['crtDivName'] = `${masterName}-Court ${existingCount + 1}`;
        }
        this.detailDataSource.data = [newRow, ...this.detailDataSource.data];
        this.selectedDetailRows = [newRow];

        if (this.config.childDetailTable) {
            this.singleSelectedDetailRow = newRow;
            this.childDetailDataSource.data = [];
            this.resetChildDetailState();
        }
        this.focusFirstInputInContainer('.detail-table-container');
    }

    handleDetailModify(): void {
        if (this.selectedDetailRows.length === 0) return;
        this.isDetailEditMode = true;
        this.selectedDetailRows.forEach(row => { row._isEdit = true; });
        this.focusFirstInputInContainer('.detail-table-container');
    }

    handleDetailCancel(): void {
        this.isDetailAddMode = false;
        this.isDetailEditMode = false;
        this.detailDataSource.data.forEach(row => row._isEdit = false);
        this.detailDataSource.data = this.detailDataSource.data.filter(row => !row._isNew);
        this.selectedDetailRows = [];
        this.singleSelectedDetailRow = null;
        this.childDetailDataSource.data = [];
    }

    handleDetailSave(): void {
        const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
            data: {
                title: 'Confirm Action',
                message: "Are you sure you want to save?",
            }, disableClose: true,
            hasBackdrop: true,
            backdropClass: 'dialog-backdrop',
        });

        dialogRef.afterClosed().subscribe((result) => {
            if (result) {
                this.executeDetailSave();
            }
        });
    }

    private executeDetailSave(): void {
        this.syncCurrentChildDetailToSelectedRow();
        let errorMessages: string[] = [];

        // ✅ Mandatory field validation + child check
        for (const row of this.detailDataSource.data) {
            if (row._isNew || row._isEdit) {

                for (const col of this.flatDetailColumns) {
                    const val = row[col.key];

                    if (
                        col.required &&
                        (val === null ||
                            val === undefined ||
                            val === '' ||
                            (typeof val === 'string' && val.trim() === ''))
                    ) {
                        errorMessages.push(`${col.label} is a mandatory field.`);
                    }
                }

                // ✅ Child table validation
                if (this.config.childDetailTable && this.singleSelectedDetailRow === row) {
                    if (this.childDetailDataSource.data.length === 0) {
                        errorMessages.push(
                            `Please add at least ${this.config.childDetailTable.panelTitle} record before saving.`
                        );
                    }
                }
            }
        }

        // ✅ Duplicate validation (Detail level)
        const detailStrings = new Set<string>();

        for (const row of this.detailDataSource.data) {
            const detailObj: any = {};
            this.flatDetailColumns.forEach((c: any) => (detailObj[c.key] = row[c.key]));

            const detailStr = JSON.stringify(detailObj);

            if (detailStrings.has(detailStr)) {
                errorMessages.push(
                    `Duplicate entry found in ${this.config.detailTable.panelTitle}.`
                );
            } else {
                detailStrings.add(detailStr);
            }

            // ✅ Duplicate validation (Child level)
            if (this.config.childDetailTable && this.singleSelectedDetailRow === row) {
                const childStrings = new Set<string>();

                for (const childRow of this.childDetailDataSource.data) {
                    const childObj: any = {};
                    this.flatChildDetailColumns.forEach((c: any) => (childObj[c.key] = childRow[c.key]));

                    const childStr = JSON.stringify(childObj);

                    if (childStrings.has(childStr)) {
                        errorMessages.push(
                            `Duplicate entry found in ${this.config.childDetailTable.panelTitle}.`
                        );
                    } else {
                        childStrings.add(childStr);
                    }
                }
            }
        }

        // ✅ Remove duplicate messages
        errorMessages = [...new Set(errorMessages)];

        // ✅ Final validation check
        if (errorMessages.length > 0) {
            const errorMessage = errorMessages.join('<br>');
            this.showDialog('Information', errorMessage);
            return;
        }

        // ================= SAVE LOGIC (unchanged) =================

        const newRecs: any[] = [];
        const modRecs: any[] = [];
        const childDetailKey = this.config.detailTable.childDetailDataKey || 'childData';

        this.detailDataSource.data.forEach(row => {
            const cleanRow = { ...row };
            delete cleanRow._isNew;
            delete cleanRow._isEdit;

            if (row._isNew) {
                cleanRow.transType = 'I';

                if (this.config.childDetailTable && cleanRow[childDetailKey] && Array.isArray(cleanRow[childDetailKey])) {
                    cleanRow[childDetailKey] = cleanRow[childDetailKey].map((cd: any) => {
                        let childRow = { ...cd, transType: 'I' };
                        childRow = this.processPayloadDates(childRow, this.flatChildDetailColumns);
                        return childRow;
                    });
                }

                const formattedDetail = this.processPayloadDates(cleanRow, this.flatDetailColumns);
                newRecs.push(formattedDetail);

            } else if (row._isEdit) {
                cleanRow.transType = cleanRow.transType || 'U';

                if (this.config.childDetailTable && cleanRow[childDetailKey] && Array.isArray(cleanRow[childDetailKey])) {
                    cleanRow[childDetailKey] = cleanRow[childDetailKey].map((cd: any) => {
                        let childRow = { ...cd, transType: cd.transType || 'U' };
                        childRow = this.processPayloadDates(childRow, this.flatChildDetailColumns);
                        return childRow;
                    });
                }

                const formattedDetail = this.processPayloadDates(cleanRow, this.flatDetailColumns);
                modRecs.push(formattedDetail);
            }
        });

        // ✅ Sync child data
        if (this.singleSelectedDetailRow && this.config.childDetailTable) {
            this.singleSelectedDetailRow[childDetailKey] = this.childDetailDataSource.data.map(r => {
                const c = { ...r };
                delete c._isNew;
                delete c._isEdit;
                return c;
            });
        }

        // ✅ Sync master data
        if (this.singleSelectedMasterRow) {
            const detailKey = this.config.masterTable.detailDataKey;
            this.singleSelectedMasterRow[detailKey] = this.detailDataSource.data.map(r => {
                const c = { ...r };
                delete c._isNew;
                delete c._isEdit;
                return c;
            });
        }

        // ✅ Skip API if master is new
        if (this.singleSelectedMasterRow && this.singleSelectedMasterRow._isNew) {
            this.detailDataSource.data.forEach(row => { row._isEdit = false; row._isNew = false; });
            this.isDetailAddMode = false;
            this.isDetailEditMode = false;
            return;
        }

        let addPayload = this.config.payloadBuilders?.insertDetail
            ? this.config.payloadBuilders.insertDetail(this.singleSelectedMasterRow, newRecs)
            : newRecs;

        let updatePayload = this.config.payloadBuilders?.updateDetail
            ? this.config.payloadBuilders.updateDetail(this.singleSelectedMasterRow, modRecs)
            : modRecs;

        const payload: ISearchTableSavePayload = {
            newRecords: addPayload,
            modifiedRecords: updatePayload,
            deletedRecords: []
        };

        this.onDetailSave.emit({ masterRow: this.singleSelectedMasterRow, payload });

        if (this.isDetailEditMode && this.config.apiEndpoints?.updateDetail) {
            this.executeApiCall(this.config.apiEndpoints.updateDetail, updatePayload, this.config.apiEndpoints.updateDetailMethod, 'detail');

        } else if (this.config.apiEndpoints?.saveDetail) {
            let combinedPayload = payload;

            if (this.config.payloadBuilders?.saveDetail) {
                combinedPayload = this.config.payloadBuilders.saveDetail(
                    this.singleSelectedMasterRow,
                    newRecs,
                    modRecs,
                    []
                );
            }

            this.executeApiCall(this.config.apiEndpoints.saveDetail, combinedPayload, this.config.apiEndpoints.saveDetailMethod, 'detail');

        } else {
            if (this.config.apiEndpoints?.addDetail && newRecs.length > 0) {
                this.executeApiCall(this.config.apiEndpoints.addDetail, addPayload, this.config.apiEndpoints.addDetailMethod, 'detail');
            }

            if (this.config.apiEndpoints?.updateDetail && modRecs.length > 0) {
                this.executeApiCall(this.config.apiEndpoints.updateDetail, updatePayload, this.config.apiEndpoints.updateDetailMethod, 'detail');
            }
        }

        this.detailDataSource.data.forEach(row => { row._isEdit = false; row._isNew = false; });
        this.isDetailAddMode = false;
        this.isDetailEditMode = false;
    }

    handleDetailDelete(): void {
        if (this.selectedDetailRows.length === 0) return;

        const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
            data: {
                title: 'Confirm Action',
                message: "Are you sure you want to delete?",
            }, disableClose: true,
            hasBackdrop: true,
            backdropClass: 'dialog-backdrop',
        });

        dialogRef.afterClosed().subscribe((result) => {
            if (result) {
                this.executeDetailDelete();
            }
        });
    }

    private executeDetailDelete(): void {
        const delRecs = this.selectedDetailRows.map(row => {
            const cleanRow = { ...row };
            delete cleanRow._isNew; delete cleanRow._isEdit;
            return cleanRow;
        });

        this.detailDataSource.data = this.detailDataSource.data.filter(r => !this.selectedDetailRows.includes(r));
        const deletedDetailRows = [...this.selectedDetailRows];
        this.selectedDetailRows = [];
        this.singleSelectedDetailRow = null;
        this.childDetailDataSource.data = [];

        // Update master
        if (this.singleSelectedMasterRow) {
            const detailKey = this.config.masterTable.detailDataKey;
            this.singleSelectedMasterRow[detailKey] = this.detailDataSource.data.map(r => {
                const c = { ...r };
                delete c._isNew;
                delete c._isEdit;
                return c;
            });
        }

        if (this.singleSelectedMasterRow && this.singleSelectedMasterRow._isNew) {
            return;
        }

        let deletePayload = this.config.payloadBuilders?.deleteDetail
            ? this.config.payloadBuilders.deleteDetail(this.singleSelectedMasterRow, delRecs)
            : (this.config.payloadBuilders?.deleteMaster ? (this.config.payloadBuilders.deleteMaster as any)([this.singleSelectedMasterRow], deletedDetailRows, []) : delRecs);

        this.onDetailSave.emit({ masterRow: this.singleSelectedMasterRow, payload: { newRecords: [], modifiedRecords: [], deletedRecords: deletePayload } });

        const endpoint = this.config.apiEndpoints?.deleteDetail || this.config.apiEndpoints?.deleteMaster;
        const method = this.config.apiEndpoints?.deleteDetail ? this.config.apiEndpoints.deleteDetailMethod : (this.config.apiEndpoints?.deleteMasterMethod || 'POST');

        if (endpoint) {
            this.executeApiCall(endpoint, deletePayload, method, 'detail', 'delete');
        }
    }

    // --- Child Detail Table Interactions ---
    toggleChildDetailSelection(row: any, checked: boolean): void {
        if (checked) {
            this.selectedChildDetailRows = [row];
        } else {
            this.selectedChildDetailRows = this.selectedChildDetailRows.filter(r => r !== row);
        }
    }

    isChildDetailSelected(row: any): boolean {
        return this.selectedChildDetailRows.includes(row);
    }

    handleChildDetailAdd(): void {
        this.isChildDetailAddMode = true;
        const newRow: any = { _isNew: true, _isEdit: true };
        this.childDetailDataSource.data = [newRow, ...this.childDetailDataSource.data];
        this.selectedChildDetailRows = [newRow];
        this.syncCurrentChildDetailToSelectedRow();
        this.focusFirstInputInContainer('.child-detail-table-container');
    }

    handleChildDetailModify(): void {
        if (this.selectedChildDetailRows.length === 0) return;
        this.isChildDetailEditMode = true;
        this.selectedChildDetailRows.forEach(row => { row._isEdit = true; });
        this.focusFirstInputInContainer('.child-detail-table-container');
    }

    handleChildDetailCancel(): void {
        this.isChildDetailAddMode = false;
        this.isChildDetailEditMode = false;
        this.childDetailDataSource.data.forEach(row => row._isEdit = false);
        this.childDetailDataSource.data = this.childDetailDataSource.data.filter(row => !row._isNew);
        this.selectedChildDetailRows = [];
        this.syncCurrentChildDetailToSelectedRow();
    }

    handleChildDetailSave(): void {
        const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
            data: {
                title: 'Confirm Action',
                message: "Are you sure you want to save?",
            }, disableClose: true,
            hasBackdrop: true,
            backdropClass: 'dialog-backdrop',
        });

        dialogRef.afterClosed().subscribe((result) => {
            if (result) {
                this.executeChildDetailSave();
            }
        });
    }

    private executeChildDetailSave(): void {
        let isValid = true;
        let errorMessage = '';

        for (const row of this.childDetailDataSource.data) {
            if (row._isNew || row._isEdit) {
                for (const col of this.flatChildDetailColumns) {
                    const val = row[col.key];
                    if (col.required && (val === null || val === undefined || val === '' || (typeof val === 'string' && val.trim() === ''))) {
                        isValid = false;
                        errorMessage = `${col.label} is a mandatory field.`;
                        break;
                    }
                }
            }
            if (!isValid) break;
        }

        if (!isValid) {
            this.showDialog('Validation Error', errorMessage);
            return;
        }

        const childStrings = new Set<string>();
        for (const row of this.childDetailDataSource.data) {
            const childObj: any = {};
            this.flatChildDetailColumns.forEach((c: any) => childObj[c.key] = row[c.key]);
            const childStr = JSON.stringify(childObj);
            if (childStrings.has(childStr)) {
                this.showDialog('Information', `Duplicate entry found in ${this.config.childDetailTable?.panelTitle}. Cannot save.`);
                return;
            }
            childStrings.add(childStr);
        }

        const newRecs: any[] = [];
        const modRecs: any[] = [];

        this.childDetailDataSource.data.forEach(row => {
            const cleanRow = { ...row };
            delete cleanRow._isNew;
            delete cleanRow._isEdit;

            if (row._isNew) {
                cleanRow.transType = 'I';
                const formattedChild = this.processPayloadDates(cleanRow, this.flatChildDetailColumns);
                newRecs.push(formattedChild);
            } else if (row._isEdit) {
                cleanRow.transType = cleanRow.transType || 'U';
                const formattedChild = this.processPayloadDates(cleanRow, this.flatChildDetailColumns);
                modRecs.push(formattedChild);
            }
        });

        // Update the detail row's data property so it sticks when selecting again
        if (this.singleSelectedDetailRow) {
            const childDetailKey = this.config.detailTable.childDetailDataKey || 'childData';
            this.singleSelectedDetailRow[childDetailKey] = this.childDetailDataSource.data.map(r => {
                const c = { ...r };
                delete c._isNew;
                delete c._isEdit;
                return c;
            });

            // Wait, also update the master row's data since the detail row is modified deeply
            if (this.singleSelectedMasterRow) {
                const detailKey = this.config.masterTable.detailDataKey;
                this.singleSelectedMasterRow[detailKey] = this.detailDataSource.data.map(r => {
                    const c = { ...r };
                    delete c._isNew;
                    delete c._isEdit;
                    return c;
                });
            }
        }

        if (this.singleSelectedDetailRow && (this.singleSelectedDetailRow._isNew || (this.singleSelectedMasterRow && this.singleSelectedMasterRow._isNew))) {
            this.childDetailDataSource.data.forEach(row => { row._isEdit = false; row._isNew = false; });
            this.isChildDetailAddMode = false;
            this.isChildDetailEditMode = false;
            return;
        }

        let addPayload = this.config.payloadBuilders?.insertChildDetail ? this.config.payloadBuilders.insertChildDetail(this.singleSelectedDetailRow, newRecs) : newRecs;
        let updatePayload = this.config.payloadBuilders?.updateChildDetail ? this.config.payloadBuilders.updateChildDetail(this.singleSelectedDetailRow, modRecs) : modRecs;

        const payload: ISearchTableSavePayload = { newRecords: addPayload, modifiedRecords: updatePayload, deletedRecords: [] };

        if (this.isChildDetailEditMode && this.config.apiEndpoints?.updateChildDetail) {
            this.executeApiCall(this.config.apiEndpoints.updateChildDetail, updatePayload, this.config.apiEndpoints.updateChildDetailMethod, 'childDetail');
        } else if (this.config.apiEndpoints?.saveChildDetail) {
            let combinedPayload = payload;
            if (this.config.payloadBuilders?.saveChildDetail) combinedPayload = this.config.payloadBuilders.saveChildDetail(this.singleSelectedDetailRow, newRecs, modRecs, []);
            this.executeApiCall(this.config.apiEndpoints.saveChildDetail, combinedPayload, this.config.apiEndpoints.saveChildDetailMethod, 'childDetail');
        } else {
            if (this.config.apiEndpoints?.addChildDetail && newRecs.length > 0) this.executeApiCall(this.config.apiEndpoints.addChildDetail, addPayload, this.config.apiEndpoints.addChildDetailMethod, 'childDetail');
            if (this.config.apiEndpoints?.updateChildDetail && modRecs.length > 0) this.executeApiCall(this.config.apiEndpoints.updateChildDetail, updatePayload, this.config.apiEndpoints.updateChildDetailMethod, 'childDetail');
        }

        this.childDetailDataSource.data.forEach(row => { row._isEdit = false; row._isNew = false; });
        this.isChildDetailAddMode = false;
        this.isChildDetailEditMode = false;
    }

    handleChildDetailDelete(): void {
        if (this.selectedChildDetailRows.length === 0) return;

        const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
            data: {
                title: 'Confirm Action',
                message: "Are you sure you want to delete the selected records?",
            }, disableClose: true,
            hasBackdrop: true,
            backdropClass: 'dialog-backdrop',
        });

        dialogRef.afterClosed().subscribe((result) => {
            if (result) {
                this.executeChildDetailDelete();
            }
        });
    }

    private executeChildDetailDelete(): void {
        const delRecs = this.selectedChildDetailRows.map(row => {
            const cleanRow = { ...row };
            delete cleanRow._isNew; delete cleanRow._isEdit;
            return cleanRow;
        });

        const deletedChildRows = [...this.selectedChildDetailRows];
        this.childDetailDataSource.data = this.childDetailDataSource.data.filter(r => !this.selectedChildDetailRows.includes(r));
        this.selectedChildDetailRows = [];

        // Update detail and master
        if (this.singleSelectedDetailRow) {
            const childDetailKey = this.config.detailTable.childDetailDataKey || 'childData';
            this.singleSelectedDetailRow[childDetailKey] = this.childDetailDataSource.data.map(r => {
                const c = { ...r };
                delete c._isNew;
                delete c._isEdit;
                return c;
            });

            if (this.singleSelectedMasterRow) {
                const detailKey = this.config.masterTable.detailDataKey;
                this.singleSelectedMasterRow[detailKey] = this.detailDataSource.data.map(r => {
                    const c = { ...r };
                    delete c._isNew;
                    delete c._isEdit;
                    return c;
                });
            }
        }

        if (this.singleSelectedDetailRow && this.singleSelectedDetailRow._isNew) {
            return;
        }
        if (this.singleSelectedMasterRow && this.singleSelectedMasterRow._isNew) {
            return;
        }

        let deletePayload = this.config.payloadBuilders?.deleteChildDetail
            ? this.config.payloadBuilders.deleteChildDetail(this.singleSelectedDetailRow, delRecs)
            : (this.config.payloadBuilders?.deleteMaster ? (this.config.payloadBuilders.deleteMaster as any)([this.singleSelectedMasterRow], [this.singleSelectedDetailRow], deletedChildRows) : delRecs);

        const endpoint = this.config.apiEndpoints?.deleteChildDetail || this.config.apiEndpoints?.deleteMaster;
        const method = this.config.apiEndpoints?.deleteChildDetail ? this.config.apiEndpoints.deleteChildDetailMethod : (this.config.apiEndpoints?.deleteMasterMethod || 'POST');

        if (endpoint) {
            this.executeApiCall(endpoint, deletePayload, method, 'childDetail', 'delete');
        }
    }

    private executeApiCall(endpoint: string, payload: any, method: 'GET' | 'POST' = 'POST', scope: 'master' | 'detail' | 'childDetail', action: 'save' | 'delete' = 'save'): void {
        this.isLoading = true;
        const req = method === 'GET' ? this.get<any>(endpoint, { params: payload }) : this.post<any, any>(endpoint, payload);
        req.subscribe({
            next: (res: any) => {
                this.isLoading = false;
                this.showDialog('Success', res.message || `${scope.charAt(0).toUpperCase() + scope.slice(1)} data ${action}d successfully`);
                if (scope === 'master') {
                    this.handleSearch();
                } else {
                    this.handleSearch();
                }
            },
            error: (err: any) => {
                console.error(`API Failed: ${endpoint}`, err);
                this.isLoading = false;
                this.showDialog('Error', `Failed to ${action} ${scope} data.`);
            }
        });
    }

    // --- Dynamic Column Dependents ---
    onSelectChange(event: any, element: any, col: any): void {
        if (!col.dependents || !col.options) return;

        const selectedValue = event.value;
        const option = col.options.find((opt: { value: any, originalObject?: any }) => opt.value === selectedValue);

        if (option && option.originalObject) {
            col.dependents.forEach((dep: { sourceProperty: string, targetKey: string }) => {
                element[dep.targetKey] = option.originalObject[dep.sourceProperty];
            });
        } else {
            // Clear dependent keys if selection is cleared or no original object exists
            col.dependents.forEach((dep: { sourceProperty: string, targetKey: string }) => {
                element[dep.targetKey] = null;
            });
        }
    }

    isColumnEditable(element: any, col: any): boolean {
        if (!element._isEdit) return false;
        if (element._isNew) {
            return col.editableOnAdd !== undefined ? col.editableOnAdd : col.isEditable;
        } else {
            return col.editableOnModify !== undefined ? col.editableOnModify : col.isEditable;
        }
    }

    allowOnlyValidChars(event: KeyboardEvent): void {
        const pattern = /[a-zA-Z0-9 \-\/]/;
        const inputChar = String.fromCharCode(event.charCode);

        if (!pattern.test(inputChar)) {
            event.preventDefault();
        }
    }
}
